'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  MapPin,
  Phone,
  Store,
  ArrowLeft,
  ArrowRight,
  Info,
  Tag,
  ShoppingBasket,
  ShieldCheck,
} from 'lucide-react';
import placesBrowsePublicService, {
  PlaceListing,
  type PlaceCategory,
  type CategoryEnrichmentResponse,
} from '@/services/PlacesBrowsePublicService';
import SuggestBusinessCta from '@/components/directory/SuggestBusinessCta';
import AddBusinessCta from '@/components/directory/AddBusinessCta';
import CategoryBrowseTracker from '@/components/tracking/CategoryBrowseTracker';
import { PoweredByFooter } from '@/components/PoweredByFooter';
import { MarketIntelSurfaceSidebar } from '@/components/place/MarketIntelSurfaceSidebar';
import { MarketIntelBanner } from '@/components/place/MarketIntelBanner';
import type { CategoryMarketIntelTeaser } from '@/services/MarketIntelSurfaceService';
import { reportShelfListingClick } from '@/services/DirectoryPresencePublicService';
import { resolveShelfForLabel, shelfHrefFor } from '@/lib/place-shelves';
import { getPlaceCityShelfUrl } from '@/utils/slug';

interface PlaceCategoryClientProps {
  categorySlug: string;
  city?: string;
  state?: string;
  marketIntelTeaser?: CategoryMarketIntelTeaser | null;
  /** Server-rendered first page of the shelf — spec §4.4.1. */
  initialPlaces?: PlaceListing[] | null;
}

function formatCategoryName(slug: string): string {
  return decodeURIComponent(slug)
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export default function PlaceCategoryClient({
  categorySlug,
  city,
  state,
  marketIntelTeaser,
  initialPlaces,
}: PlaceCategoryClientProps) {
  const [places, setPlaces] = useState<PlaceListing[]>(initialPlaces ?? []);
  const [enrichment, setEnrichment] = useState<CategoryEnrichmentResponse | null>(null);
  const [shelfIndex, setShelfIndex] = useState<PlaceCategory[]>([]);
  const [loading, setLoading] = useState(!initialPlaces);
  const [error, setError] = useState<string | null>(null);

  const categoryName = enrichment?.market?.categoryName ?? formatCategoryName(categorySlug);

  useEffect(() => {
    const fetchData = async () => {
      // The server prefilled the listings — still fetch enrichment + the shelf
      // index (they drive the body copy, JSON-LD, and shelf chips), but skip
      // the places call so the prefilled grid never flashes a spinner.
      const needsPlaces = !initialPlaces;
      try {
        if (needsPlaces) setLoading(true);
        const [placesData, enrichmentData, shelfData] = await Promise.all([
          needsPlaces
            ? placesBrowsePublicService.getPlacesByCategory(categorySlug, city)
            : Promise.resolve(null),
          // No city → national category view; fetch the '__all__' packet.
          placesBrowsePublicService.getCategoryEnrichment(categorySlug, city ?? '__all__', state),
          // Live shelf index — resolves taxonomy labels to real shelves with
          // canonical slugs + per-market listing counts.
          placesBrowsePublicService.getCategories(),
        ]);
        if (placesData) {
          setPlaces(placesData.places);
        } else if (needsPlaces) {
          setError('Failed to load places.');
        }
        setEnrichment(enrichmentData);
        setShelfIndex(shelfData?.categories ?? []);
      } catch {
        if (needsPlaces) setError('Failed to load places.');
      } finally {
        if (needsPlaces) setLoading(false);
      }
    };
    fetchData();
  }, [categorySlug, city, state, initialPlaces]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
        <p className="text-neutral-600 dark:text-neutral-400">{error}</p>
      </div>
    );
  }

  // Group by city for the city filter chips; capture state for disambiguation
  const cityCounts: Record<string, number> = {};
  const cityStateMap: Record<string, string> = {};
  for (const place of places) {
    const key = place.city || 'Unknown';
    cityCounts[key] = (cityCounts[key] || 0) + 1;
    if (place.state && !cityStateMap[key]) {
      cityStateMap[key] = place.state;
    }
  }
  const cities = Object.entries(cityCounts).sort((a, b) => b[1] - a[1]);

  // Taxonomy label → live shelf for this market (see lib/place-shelves).
  const shelfForLabel = (label: string) => resolveShelfForLabel(label, city, state, shelfIndex);
  const shelfHref = (slug: string) => shelfHrefFor(slug, city, state);

  const jsonLd = enrichment
    ? {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: enrichment.effective.metaTitle || categoryName,
        description: enrichment.effective.description,
        url:
          typeof window !== 'undefined'
            ? window.location.href
            : `https://visibleshelf.com/place/category/${categorySlug}`,
      }
    : null;

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <CategoryBrowseTracker
        categoryId={categorySlug}
        categorySlug={categorySlug}
        categoryName={categoryName}
        pageType="directory_category"
        surface="place"
        city={city}
        state={state}
        filterSignature={city ?? ''}
      />
      {jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
      {/* Header */}
      <div className="bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400 mb-4">
            <Link href="/directory" className="hover:text-neutral-700 dark:hover:text-neutral-200">
              Directory
            </Link>
            <span>/</span>
            <Link href="/place" className="hover:text-neutral-700 dark:hover:text-neutral-200">
              Places
            </Link>
            <span>/</span>
            <span className="text-neutral-900 dark:text-neutral-100 font-medium">
              {categoryName}
            </span>
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <Store className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900 dark:text-neutral-100">
                  {categoryName}
                </h1>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                  {places.length} {places.length === 1 ? 'place' : 'places'} listed
                  {city ? ` in ${city}` : ''}
                </p>
              </div>
            </div>
            <Link
              href="/place"
              className="inline-flex items-center text-sm text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              All categories
            </Link>
          </div>

          {/* Page intro — held in its own panel so the shelf copy reads as a
              distinct block rather than trailing the title. */}
          {(enrichment?.bodyCopy || enrichment?.effective?.description) && (
            <div className="mt-6 max-w-3xl rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/40 p-5 sm:p-6">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mb-2">
                Overview
              </p>
              <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed whitespace-pre-line">
                {enrichment?.bodyCopy ?? enrichment?.effective?.description}
              </p>
              {enrichment?.effective?.synonyms && enrichment.effective.synonyms.length > 0 && (
                <p className="mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-700 text-sm text-neutral-500 dark:text-neutral-400">
                  Related: {enrichment.effective.synonyms.join(', ')}
                </p>
              )}
            </div>
          )}

          {/* City filter chips */}
          {cities.length > 1 && (
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                href={`/place/category/${categorySlug}`}
                className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full transition-colors ${
                  !city
                    ? 'bg-blue-600 text-white'
                    : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                }`}
              >
                All cities
              </Link>
              {cities.map(([cityName, count]) => {
                const stateForCity = cityStateMap[cityName];
                const stateQs = stateForCity ? `&state=${encodeURIComponent(stateForCity)}` : '';
                return (
                  <Link
                    key={cityName}
                    href={`/place/category/${categorySlug}?city=${encodeURIComponent(cityName)}${stateQs}`}
                    className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full transition-colors ${
                      city === cityName
                        ? 'bg-blue-600 text-white'
                        : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-600'
                    }`}
                  >
                    <MapPin className="w-3 h-3" />
                    {cityName}
                    <span className="font-medium">{count}</span>
                  </Link>
                );
              })}
            </div>
          )}

          {/* City-shelf hop — with a city filter active, link to the
              cross-category seed shelf for that market (/place/city). */}
          {city && (
            <div className="mt-3">
              <Link
                href={getPlaceCityShelfUrl(city, cityStateMap[city] ?? state)}
                className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
              >
                Browse all places in {city}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Places list */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {places.length === 0 ? (
          <div className="text-center py-16">
            <Store className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
              No Places in {categoryName}
              {city ? ` in ${city}` : ''}
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
              We don't have any published listings in this category yet.
            </p>
            <Link
              href="/place"
              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Browse all categories
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {places.map((place) => (
              <PlaceCard
                key={place.id}
                place={place}
                categoryName={categoryName}
                shelfRef={`place/category/${categorySlug}`}
              />
            ))}
          </div>
        )}

        {/* Square banner slot (300x250) — in-feed placement at the end of the
            listing grid, before the listings disclosure. */}
        {places.length > 0 && (
          <div className="mt-10 flex justify-center">
            <MarketIntelBanner
              variant="square"
              surfaceType="category"
              teaser={marketIntelTeaser}
            />
          </div>
        )}

        {/* Info banner */}
        <div className="mt-12 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                About These Listings
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                These places are listed from public information (address, phone, and
                publicly available data). They are not claimed profiles. If you own one
                of these businesses, claim it free to fix your details, add photos, and
                showcase 5 top sellers from your own dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Enrichment Content: About + Shopper Guide + Hierarchy + FAQ */}
      {(enrichment?.shopperGuide ||
        enrichment?.context?.category_overview ||
        (enrichment?.context?.sub_categories && enrichment.context.sub_categories.length > 0) ||
        (enrichment?.context?.adjacent_categories && enrichment.context.adjacent_categories.length > 0) ||
        (enrichment?.faq && enrichment.faq.length > 0)) && (
        <div className="bg-neutral-50 dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-800">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6">
            {/* About this category */}
            {enrichment?.context?.category_overview && (
              <section className="max-w-3xl rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-5 sm:p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">
                  About {categoryName}
                </h2>
                <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed whitespace-pre-line">
                  {enrichment.context.category_overview}
                </p>
              </section>
            )}

            {/* Shopper Guide */}
            {enrichment?.shopperGuide && (
              <section className="max-w-3xl rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-5 sm:p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">
                  What to Look For
                </h2>
                <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed whitespace-pre-line">
                  {enrichment.shopperGuide}
                </p>
              </section>
            )}

            {/* FAQ */}
            {enrichment?.faq && enrichment.faq.length > 0 && (
              <section className="max-w-3xl rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-5 sm:p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-1">
                  Frequently Asked Questions
                </h2>
                <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                  {enrichment.faq.map((item, idx) => (
                    <div key={idx} className="py-4 last:pb-0">
                      <h3 className="font-semibold text-neutral-900 dark:text-white mb-1">{item.question}</h3>
                      <p className="text-neutral-700 dark:text-neutral-300 text-sm leading-relaxed">{item.answer}</p>
                    </div>
                  ))}
                </div>
                {/* FAQ Schema */}
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                      '@context': 'https://schema.org',
                      '@type': 'FAQPage',
                      mainEntity: enrichment.faq.map((item) => ({
                        '@type': 'Question',
                        name: item.question,
                        acceptedAnswer: {
                          '@type': 'Answer',
                          text: item.answer,
                        },
                      })),
                    }),
                  }}
                />
              </section>
            )}

            {/* Sub-categories */}
            {enrichment?.context?.sub_categories && enrichment.context.sub_categories.length > 0 && (
              <section className="max-w-3xl rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-5 sm:p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">
                  Browse {categoryName} by Type
                </h2>
                <div className="flex flex-wrap gap-2">
                  {enrichment.context.sub_categories.map((sub) => (
                    <ShelfChip key={sub} label={sub} shelf={shelfForLabel(sub)} hrefFor={shelfHref} />
                  ))}
                </div>
              </section>
            )}

            {/* Related categories */}
            {enrichment?.context?.adjacent_categories && enrichment.context.adjacent_categories.length > 0 && (
              <section className="max-w-3xl rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-5 sm:p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">
                  Related Categories
                </h2>
                <div className="flex flex-wrap gap-2">
                  {enrichment.context.adjacent_categories.map((adj) => (
                    <ShelfChip key={adj} label={adj} shelf={shelfForLabel(adj)} hrefFor={shelfHref} />
                  ))}
                </div>
              </section>
            )}

            {/* Secondary categories — overlapping shelves this category is filed under */}
            {enrichment?.effective?.secondaryCategories && enrichment.effective.secondaryCategories.length > 0 && (
              <section className="max-w-3xl rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-5 sm:p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">
                  Also Filed Under
                </h2>
                <div className="flex flex-wrap gap-2">
                  {enrichment.effective.secondaryCategories.map((label) => (
                    <ShelfChip key={label} label={label} shelf={shelfForLabel(label)} hrefFor={shelfHref} />
                  ))}
                </div>
              </section>
            )}

            {/* Super categories — broader parent shelves */}
            {enrichment?.context?.super_categories && enrichment.context.super_categories.length > 0 && (
              <section className="max-w-3xl rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-5 sm:p-6">
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-3">
                  Broader Categories
                </h2>
                <div className="flex flex-wrap gap-2">
                  {enrichment.context.super_categories.map((label) => (
                    <ShelfChip key={label} label={label} shelf={shelfForLabel(label)} hrefFor={shelfHref} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      )}

      {/* Footer CTAs — sit directly above the platform footer. */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        <AddBusinessCta
          category={categoryName}
          source={`/place/category/${categorySlug}`}
        />
        <SuggestBusinessCta
          category={categoryName}
          source={`/place/category/${categorySlug}`}
        />
      </div>

      <PoweredByFooter />

      {/* Market Intel sidebar (§12.3) — server-rendered teaser for SEO. */}
      <MarketIntelSurfaceSidebar
        surfaceType="category"
        surfaceKey={categorySlug}
        city={city ?? '__all__'}
        state={state ?? null}
        initialTeaser={marketIntelTeaser}
      />
    </div>
  );
}

// ====================
// ShelfChip — taxonomy label that links to its live shelf when one exists
// ====================

function ShelfChip({
  label,
  shelf,
  hrefFor,
}: {
  label: string;
  shelf: { slug: string; count: number } | null;
  hrefFor: (slug: string) => string;
}) {
  const base =
    'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border';

  // Cold chip — label doesn't resolve to a live shelf in this market.
  if (!shelf) {
    return (
      <span className={`${base} border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/40 text-neutral-500 dark:text-neutral-400`}>
        {label}
      </span>
    );
  }

  // Hot chip — links to a live shelf, styled as the platform's link-chip
  // vocabulary so the tap target is visible at a glance.
  return (
    <Link
      href={hrefFor(shelf.slug)}
      className={`${base} border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 hover:border-blue-300 dark:hover:bg-blue-900/50 dark:hover:border-blue-700 transition-colors`}
    >
      {label}
      <span className="text-xs font-semibold text-blue-500 dark:text-blue-400">{shelf.count}</span>
    </Link>
  );
}

// ====================
// PlaceCard — compact card for category browse pages
// ====================

function PlaceCard({
  place,
  categoryName,
  shelfRef,
}: {
  place: PlaceListing;
  categoryName: string;
  shelfRef: string;
}) {
  const fullAddress = [place.address, place.city, place.state, place.zipCode]
    .filter(Boolean)
    .join(', ');

  const claimHref = place.claimToken
    ? `/place/claim/${place.claimToken}`
    : '#claim-inquiry';

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden hover:shadow-md transition-shadow">
      {/* Logo or placeholder */}
      <div className="h-32 bg-gradient-to-br from-blue-50 to-neutral-100 dark:from-blue-900/20 dark:to-neutral-700 flex items-center justify-center">
        {place.logoUrl ? (
          <img
            src={place.logoUrl}
            alt={place.businessName}
            className="h-full w-full object-cover"
          />
        ) : (
          <Store className="w-10 h-10 text-neutral-400" />
        )}
      </div>

      {/* Body */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <Link
            href={`/place/${place.slug}?shelf=${encodeURIComponent(shelfRef)}`}
            onClick={() => reportShelfListingClick(shelfRef)}
            className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 hover:text-blue-600 dark:hover:text-blue-400"
          >
            {place.businessName}
          </Link>
          {place.snapEbtReported && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 font-medium flex-shrink-0">
              <ShoppingBasket className="w-3 h-3" />
              SNAP/EBT
            </span>
          )}
        </div>

        {(place.deliveryOffered || place.pickupOffered) && (
          <div className="flex flex-wrap gap-1 mb-2">
            {place.deliveryOffered && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                Delivery
              </span>
            )}
            {place.pickupOffered && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                Pickup
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400 mb-3">
          <Tag className="w-3 h-3" />
          {categoryName}
        </div>

        {place.description && (
          <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2 mb-3">
            {place.description}
          </p>
        )}

        {/* Location + phone */}
        <div className="space-y-1.5 text-sm text-neutral-600 dark:text-neutral-400">
          {fullAddress && (
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{fullAddress}</span>
            </div>
          )}
          {place.phone && (
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 flex-shrink-0" />
              <a
                href={`tel:${place.phone}`}
                className="hover:text-neutral-900 dark:hover:text-neutral-200"
              >
                {place.phone}
              </a>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-700 flex items-center justify-between">
          <Link
            href={`/place/${place.slug}?shelf=${encodeURIComponent(shelfRef)}`}
            onClick={() => reportShelfListingClick(shelfRef)}
            className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 inline-flex items-center"
          >
            View details
            <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
          {place.claimToken && (
            <Link
              href={claimHref}
              title="Claim to verify your details and appear on every matching category shelf"
              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              Claim this listing
            </Link>
          )}
        </div>

        {/* Disclaimer */}
        {place.publicDisclaimer && (
          <p className="mt-3 text-xs text-neutral-400 dark:text-neutral-500 italic">
            {place.publicDisclaimer}
          </p>
        )}
      </div>
    </div>
  );
}
